import React, { useEffect, useState, useRef  } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  People as PeopleIcon,
  School as SchoolIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { studentService, paymentService, reportService } from '../services/api';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import ChartContainer from '../components/ChartContainer';
import BalanceWidgetSimple from '../components/BalanceWidgetSimple';
import { formatNumber, formatCurrency, formatYAxisTick } from '../utils/formatNumber';

interface DashboardStats {
  totalStudents: number;
  activeStudents: number;
  totalParents: number;
  monthlyIncome: number;
  monthlyExpenses: number;
  pendingPayments: number;
  studentsWithoutPayment: number;
  teacherPayments: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recentPayments, setRecentPayments] = useState<any[]>([]);
  const [upcomingBajas, setUpcomingBajas] = useState<any[]>([]);
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [paymentDistribution, setPaymentDistribution] = useState<any[]>([]);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartDimensions, setChartDimensions] = useState({ width: 0, height: 0 });

  const currentMonth = new Date().toLocaleString('es-ES', { month: 'long' });
  const capitalizedMonth = currentMonth.charAt(0).toUpperCase() + currentMonth.slice(1)
  const currentYear = new Date().getFullYear();

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Cargar datos en paralelo
      const [
        studentsRes,
        paymentsRes,
        reportRes,
        bajaRes,
        monthlyRes
      ] = await Promise.all([
        studentService.getAll({ per_page: 1 }),
        paymentService.getAllMonthly({ per_page: 5, status: 'pending' }),
        reportService.getMonthlySummary({ month: capitalizedMonth, year: currentYear }),
        reportService.getStudentsWithoutPayment({ months: 2 }),
        paymentService.getMonthlySummary(currentMonth, currentYear)
      ]);

      // Procesar estadísticas
      const totalStudents = studentsRes.data.total || 0;
      const activeStudents = studentsRes.data.data?.filter((s: any) => s.status === 'active').length || 0;
      
      setStats({
        totalStudents,
        activeStudents,
        totalParents: 0, // Se necesita endpoint específico
        monthlyIncome: reportRes.data?.student_payments?.total_paid || 0,
        monthlyExpenses: reportRes.data?.expenses_total || 0,
        pendingPayments: paymentsRes.data?.data?.length || 0,
        studentsWithoutPayment: reportRes.data?.students_without_payment || 0,
        teacherPayments: reportRes.data?.teacher_payments?.total_paid || 0
      });

      setRecentPayments(paymentsRes.data?.data || []);
      setUpcomingBajas(bajaRes.data?.students || []);
      setMonthlyData(monthlyRes.data?.summary || {});

      // Datos para gráficos
      setPaymentDistribution([
        { name: 'Pagados', value: reportRes.data?.student_payments?.total_paid || 0 },
        { name: 'Por Cuotas', value: reportRes.data?.student_payments?.total_partial || 0 }
      ]);

    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();

     const updateDimensions = () => {
      if (chartContainerRef.current) {
        setChartDimensions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const StatCard = ({ 
    title, 
    value, 
    icon, 
    color, 
    subtitle,
    action
  }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
    action?: () => void;
  }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          <Box sx={{ color }}>
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" component="div">
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
      {action && (
        <CardActions>
          <Button size="small" onClick={action}>
            Ver detalles <ArrowForwardIcon sx={{ ml: 1 }} fontSize="small" />
          </Button>
        </CardActions>
      )}
    </Card>
  );

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Información General
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadDashboardData}
          variant="outlined"
        >
          Actualizar
        </Button>
      </Box>

      <Typography variant="h6" color="text.secondary" mb={2}>
        Bienvenido, {user?.name}
      </Typography>

      {/* Estadísticas principales */}
      <Grid container spacing={3} mb={4}>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <BalanceWidgetSimple onRefresh={loadDashboardData} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Estudiantes Activos"
            value={stats?.activeStudents || 0}
            icon={<SchoolIcon />}
            color="#1976d2"
            subtitle={`de ${stats?.totalStudents || 0} total`}
            action={() => navigate('/students')}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Ingresos Mes"
            value={formatNumber(stats?.monthlyIncome, 2, 'RUB') || 0}
            icon={<TrendingUpIcon />}
            color="#2e7d32"
            subtitle={currentMonth.charAt(0).toUpperCase() + currentMonth.slice(1)}
            action={() => navigate('/monthly-payments')}
          />
        </Grid>
        
        {/*<Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Pagos Pendientes"
            value={stats?.pendingPayments || 0}
            icon={<PaymentIcon />}
            color="#ed6c02"
            subtitle="Requieren atención"
            action={() => navigate('/monthly-payments?status=pending')}
          />
        </Grid>
        */}
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Próximas Bajas"
            value={upcomingBajas.length}
            icon={<WarningIcon />}
            color="#d32f2f"
            subtitle="Estudiantes en riesgo"
            action={() => navigate('/reports')}
          />
        </Grid>
      </Grid>

      {/* Gráficos y datos */}
      <Grid container spacing={3}>
        {/* Distribución de pagos */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper 
            sx={{ 
              p: 2, 
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="h6" mb={2}>
              Distribución de Pagos - {currentMonth}
            </Typography>
            <Box sx={{ flex: 1, minHeight: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={paymentDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => {
                      if (percent === undefined) return name || '';
                      return `${name}: ${(percent * 100).toFixed(0)}%`;
                    }}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {paymentDistribution.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend 
                    layout="horizontal" 
                    verticalAlign="bottom" 
                    align="center"
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Balance mensual */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" mb={2}>
              Balance Mensual
            </Typography>
            <ChartContainer height={300}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { name: 'Ingresos', value: stats?.monthlyIncome || 0 },
                  { name: 'Gastos', value: stats?.monthlyExpenses || 0 },
                  { name: 'Profesores', value: stats?.teacherPayments || 0 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={(value) => formatYAxisTick(value)}/>
                  <Tooltip 
                    formatter={(value: number | undefined) => {
                      if (value === undefined) return '₽ 0';
                      return formatCurrency(value);
                    }}
                                      labelStyle={{ fontWeight: 'bold' }}
                  />
                  <Legend />
                  <Bar dataKey="value" fill="#82ca9d" name="Monto (₽)" />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Balance Neto: ${((stats?.monthlyIncome || 0) - (stats?.monthlyExpenses || 0) - (stats?.teacherPayments || 0)).toLocaleString()}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Pagos recientes */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Pagos Recientes Pendientes</Typography>
              <Button size="small" onClick={() => navigate('/monthly-payments')}>
                Ver todos
              </Button>
            </Box>
            <List>
              {recentPayments.length > 0 ? (
                recentPayments.map((payment) => (
                  <ListItem
                    key={payment.id}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => navigate(`/monthly-payments/${payment.id}`)}>
                        <ArrowForwardIcon />
                      </IconButton>
                    }
                  >
                    <ListItemIcon>
                      {payment.status === 'paid' ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="warning" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={payment.student?.full_name || 'Estudiante'}
                      secondary={`${payment.month} ${payment.year} - $${payment.amount}`}
                    />
                  </ListItem>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" align="center" py={2}>
                  No hay pagos pendientes
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Próximas bajas */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Estudiantes en Riesgo</Typography>
              <Button size="small" onClick={() => navigate('/reports')}>
                Ver reporte
              </Button>
            </Box>
            <List>
              {upcomingBajas.length > 0 ? (
                upcomingBajas.slice(0, 5).map((student) => (
                  <ListItem
                    key={student.id}
                    secondaryAction={
                      <Chip
                        label={`${student.months_without_payment} meses`}
                        color="error"
                        size="small"
                      />
                    }
                  >
                    <ListItemIcon>
                      <WarningIcon color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={student.full_name}
                      secondary={`Grado: ${student.grade}`}
                    />
                  </ListItem>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" align="center" py={2}>
                  No hay estudiantes en riesgo
                </Typography>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Acciones rápidas */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" mb={2}>
              Acciones Rápidas
            </Typography>
            <Grid container spacing={2}>
              <Grid>
                <Button
                  variant="contained"
                  startIcon={<PaymentIcon />}
                  onClick={() => navigate('/monthly-payments/new')}
                >
                  Registrar Pago
                </Button>
              </Grid>
              <Grid>
                <Button
                  variant="outlined"
                  startIcon={<SchoolIcon />}
                  onClick={() => navigate('/students/new')}
                >
                  Agregar Estudiante
                </Button>
              </Grid>
              <Grid>
                <Button
                  variant="outlined"
                  startIcon={<AccountBalanceIcon />}
                  onClick={() => navigate('/teacher-payments/new')}
                >
                  Pagar Profesor
                </Button>
              </Grid>
              <Grid>
                <Button
                  variant="outlined"
                  startIcon={<AssessmentIcon />}
                  onClick={() => navigate('/reports')}
                >
                  Ver Reportes
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;